import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { Sidebar } from '../../components/Sidebar';
import api from '@/utils/ky';
import { Message } from '../../components/Message';

// Define types
interface ChatMessage {
  role: 'ai' | 'user';
  content: string;
}

interface SessionDetails {
  id: string;
  type: string;
  medium: 'voice' | 'text';
  startedAt: string;
  endedAt: string;
  context: {
    goal: string;
    theme: {
      title: string;
      reason: string;
    };
  };
  messages: ChatMessage[];
}

export function ChatHistory() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token') || '';

  // Fetch session data
  const {
    data: session,
    isLoading,
    error,
  } = useQuery<SessionDetails>({
    queryKey: ['session', sessionId],
    queryFn: () =>
      api.get(`api/sessions/${sessionId}/chat?token=${token}`).json(),
    enabled: !!sessionId && !!token,
  });

  // Fix the handleBack function to properly include the token
  const handleBack = () => {
    if (token) {
      navigate(`/en/sessions/past?token=${encodeURIComponent(token)}`);
    } else {
      navigate(`/en/sessions/past`);
      // navigate(-1);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getDate()} ${date
      .toLocaleString('default', { month: 'short' })
      .toUpperCase()} ${date.getFullYear()}`;
  };

  const calculateDuration = (start: string, end: string) => {
    const startTime = new Date(start);
    const endTime = new Date(end);
    const durationMs = endTime.getTime() - startTime.getTime();
    return Math.round(durationMs / 60000); // Minutes
  };

  const formatSessionType = (type: string, medium: string) => {
    if (type === 'role_play') {
      return medium === 'voice' ? 'VOICE ROLEPLAY' : 'TEXT ROLEPLAY';
    }
    return 'GENERAL COACHING';
  };

  return (
    <div className="min-h-screen flex">
      <Sidebar token={token} />

      <div className="flex-1 bg-gray-50 py-5 pl-64 pr-5">
        <div className="px-8">
          <div className="flex items-center mb-6">
            <button
              onClick={handleBack}
              className="mr-3 p-2 rounded-full hover:bg-gray-100"
              aria-label="Go back"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold tracking-tight">Chat History</h1>
          </div>

          {isLoading && (
            <div className="mt-4 p-6 bg-white rounded-xl border border-gray-200 flex justify-center">
              <p className="text-gray-600">Loading chat history...</p>
            </div>
          )}

          {error && (
            <div className="mt-4 p-6 bg-red-50 text-red-700 rounded-xl">
              Error loading chat history. Please try again.
            </div>
          )}

          {session && (
            <>
              {/* Messages */}
              <div className="mt-6 space-y-6 bg-white rounded-xl border border-gray-200 p-6">
                {session.messages && session.messages.length > 0 ? (
                  session.messages.map((message, index) => (
                    <Message
                      index={index}
                      message={message}
                      messages={session.messages}
                      sessionId={sessionId!}
                    />
                  ))
                ) : (
                  <p className="text-center text-gray-500">
                    No messages available for this session.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
